import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, catchError, filter, interval, map, Observable, of, pairwise, switchMap, tap, throwError } from 'rxjs';
import { jwtDecode } from 'jwt-decode';

// import * as moment from 'moment';
import moment from 'moment';

import { BooxApiService } from './boox-api.service';
import { BooxStorageService } from './boox-storage.service';
import { MatomoService } from './matomo.service';
import { Router } from '@angular/router';
import { BOOX_SESSION, BooxSessionToken } from '../token/boox-session.token';
import { BOOX_APIS, BOOX_DEFAULT_API, BOOX_ROUTES, BooxApisToken, BooxRoutesToken } from '../token';

export interface ISessionModel {
    session_token: string,
    session_id: string,
    id: number,
    name: string,
    first_name: string,
    last_name: string,
    email: string,
    is_sys_admin: boolean,
    last_login_date: string,
    host: string,
    role: string,
    role_id: number,
    impersonate?: boolean,
}

export interface ISignInCredential {
    email?: string,
    username?: string,
    password: string,
}

export interface IResetCredential {
    email: string,
    old_password?: string,
    new_password?: string,
    code?: string,
}

@Injectable({
    providedIn: 'root'
})
export class BooxSessionService {

    public $valid = new BehaviorSubject<boolean>(false);
    public $session = new BehaviorSubject<ISessionModel | null>(null);

    public sessionValidation$ = Observable<number>;
    // session valid until is a observable that emits every second, the seconds until the session is valid
    public sessionValidUntil$: Observable<number>;
    // session expired is a observable that emits when the session is expired
    public sessionExpired$: Observable<boolean>;
    // session expired soon is a observable that emits when the session is expired soon
    public sessionExpiredSoon$: Observable<boolean>;

    public get session(): ISessionModel | null {
        return this.$session.getValue();
    }

    public get isAuthenticated(): boolean {
        return !!this.session;
    }

    public get isAdmin(): boolean {
        return !!this.session && this.session.is_sys_admin;
    }

    constructor(
        private router: Router,
        private storageSrv: BooxStorageService,
        private booxSrv: BooxApiService,
        private Matomo: MatomoService,

        @Inject(BOOX_SESSION) private sessionSetting: BooxSessionToken,
        @Inject(BOOX_DEFAULT_API) private default_api: string,
        @Inject(BOOX_APIS) private apis: BooxApisToken,
        @Inject(BOOX_ROUTES) private routes: BooxRoutesToken,

    ) {
        this.$session.subscribe((session) => {
            this.$valid.next(!!session);
            this.booxSrv.setToken(session?.session_token || null);
        });



        this.sessionValidUntil$ = interval(1000)
            .pipe(
                switchMap(() => {
                    if (this.isAuthenticated && this.session) {
                        let _session = jwtDecode(this.session['session_token']);

                        const {
                            exp: expired,
                            nbf: notBefore,
                            iat: issuedAt,
                        } = _session;

                        const token = { expired, notBefore, issuedAt };

                        if (_session && _session['exp']) {
                            return of(moment.unix(_session['exp']).diff(moment(), 'seconds'));
                        }
                    }
                    return of(0);
                }),
            );


        this.sessionExpiredSoon$ = this.sessionValidUntil$.pipe(
            map((_seconds) => !!this.sessionSetting.expired_soon && _seconds < this.sessionSetting.expired_soon && _seconds > 0),
        );

        this.sessionExpired$ = this.sessionValidUntil$.pipe(
            pairwise(),
            filter(([previous, current]) => current == 0 && previous > 0),
            map(() => true),
        );

        this.sessionExpired$.subscribe((_expired) => this.sessionSetting.auto_logout && this.signOut().subscribe());
    }

    public setSession(_session: string) {

        if (!!_session) {
            this.storageSrv.setItem(this.sessionSetting.storage_key, _session);
            this.$session.next({
                ... this.session as ISessionModel,
                session_token: _session,
                session_id: _session,
            });
        }
    }

    public updateSession(_session: ISessionModel, options: { store: boolean } = { store: true }) {
        if (options.store) this.storageSrv.setItem(this.sessionSetting.storage_key, _session['session_token']);
        this.$session.next(_session);
    }

    /**
     * Get the boox config
     * @param _booxName Boox config name
     * @returns environment boox config
     */
    private _booxConfig(_booxName: string) {
        var booxName = _booxName as keyof typeof this.apis;
        return this.apis[booxName];
    }

    /**
     * Load the current session from local storage
     * @returns
     */
    public load(): Observable<ISessionModel | null> {

        let booxConfig = this._booxConfig(this.default_api);

        if (!!this.storageSrv.getItem(this.sessionSetting.storage_key, false)) {
            return this.booxSrv.get<ISessionModel>('user/session', {})
                .pipe(
                    tap((_session: ISessionModel) => this.$session.next(_session)),
                    tap(() => this.Matomo.trackEvent('session', 'load', 'success')),
                    catchError((_error) => {
                        this.storageSrv.clearItem(this.sessionSetting.storage_key);
                        this.$session.next(null);
                        this.Matomo.trackEvent('session', 'load', 'fail');
                        return of(null);
                    }),
                );
        }
        return of(null);
    }


    /**
   * Sign in and establish a user session.
   *
   * @param _credentials - User login credentials
   * @returns Observable of ISessionModel or null if sign-in fails
   */
    signIn(_credentials: ISignInCredential): Observable<ISessionModel | null> {
        // Retrieve API configuration
        const booxConfig = this._booxConfig(this.default_api);
        // Clear any existing session before authentication attempt
        this.storageSrv.clearItem(this.sessionSetting.storage_key);

        return this.booxSrv.post<ISessionModel>('user/session', _credentials, {}).pipe(
            tap((_session: ISessionModel) => {
                // Store session token in local storage
                this.storageSrv.setItem(this.sessionSetting.storage_key, _session.session_token);

                // Update session observable
                this.$session.next(_session);

                // Track successful login event
                this.Matomo.trackEvent('session', 'sign-in', 'success');

                const redirectUrl = this.storageSrv.getItem('redirectUrl', false);
                // Clear the redirect URL
                this.storageSrv.clearItem('redirectUrl');

                const targetRoute = redirectUrl ? ['/',...redirectUrl.split('/').filter(Boolean)] : this.routes.home;
                if (targetRoute) this.router.navigate(targetRoute);
            }),
            catchError((_error) => {
                // Clear session data on authentication failure
                this.storageSrv.clearItem(this.sessionSetting.storage_key);
                this.$session.next(null);

                // Track failed login event
                this.Matomo.trackEvent('session', 'sign-in', 'fail');

                // Re-throw error for further handling
                return throwError(() => _error.error);
            })
        );
    }


    /**
     * Renew the current session
     * @returns
     */
    renew(): Observable<ISessionModel | null> {

        let booxConfig = this._booxConfig(this.default_api);

        return this.booxSrv.put<ISessionModel>('user/session', {})
            .pipe(
                tap((_session: ISessionModel) => this.storageSrv.setItem(this.sessionSetting.storage_key, _session['session_token'])),
                tap((_session: ISessionModel) => this.$session.next(_session)),
                tap(() => this.Matomo.trackEvent('session', 'renew', 'success')),
                catchError((_error) => {
                    this.storageSrv.clearItem(this.sessionSetting.storage_key);
                    this.$session.next(null);
                    this.Matomo.trackEvent('session', 'renew', 'fail');
                    throw _error.error;
                }),
            );
    }

    /**
     * Sign Out
     */
    signOut() {

        let booxConfig = this._booxConfig(this.default_api);

        //
        return this.booxSrv.delete('user/session', {})
            .pipe(
                catchError((_error) => of(null)),
                tap(() => this.storageSrv.clearItem(this.sessionSetting.storage_key)),
                tap(() => this.$session.next(null)),
                tap(() => this.Matomo.trackEvent('session', 'sign-out', 'success')),
            )
    }


    /**
     * reset password
     * @returns
     */
    resetPassword(credentials: IResetCredential): Observable<any> {
        return this.booxSrv.post('user/password', credentials, { params: {  } })
            .pipe(
                tap(() => this.Matomo.trackEvent('session', 'reset-password', 'success')),
                catchError((_error) => {
                    this.Matomo.trackEvent('session', 'reset-password', 'fail');
                    throw _error.error;
                }),
            );
    }

    /**
     * request reset password
     * @returns
     */
    forgotPassword(credentials: IResetCredential): Observable<any> {
        return this.booxSrv.post('user/password', credentials, { params: { reset: true } })
            .pipe(
                tap(() => this.Matomo.trackEvent('session', 'request-reset-password', 'success')),
                catchError((_error) => {
                    this.Matomo.trackEvent('session', 'request-reset-password', 'fail');
                    throw _error.error;
                }),
            );
    }
}
